import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Spinner from '../../modules/components/global/spinner';
import ProjectCard from '../../modules/components/projects/project-card';
import Link from '../../modules/components/global/link';
import SearchBar from '../../modules/components/global/search-bar';
import ProjectsFilters from '../../modules/components/projects/project-filters';
import LoadError from '../../modules/components/global/load-error';
import api from '../../modules/services/api-service';
import { buildStudioOptions, filterProjects } from './helpers';
import useAuth from '~/modules/hooks/use-auth';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import useWindowSize from '~/modules/hooks/use-window-size';
import SlidersHorizontal from 'lucide-react/dist/esm/icons/sliders-horizontal';
import X from 'lucide-react/dist/esm/icons/x';

export const PROJECTS_QUERY = `
query getProjects($archived: Boolean) {
	projects(limit: 500, archived: $archived) {
		nodes {
			id
			name
			slug
			poster
			archived
			flags
			permissions
			studio {
				name
				slug
			}
		}
	}
	studios {
		nodes {
			name
			slug
		}
	}
}
`;

const CardFocusWrapper = styled.div`
	border-radius: 2px;
	&:focus-within {
		outline: 2px solid hsl(var(--primary));
		outline-offset: 3px;
		border-radius: 4px;
	}
`;

const DEFAULT_FILTERS = {
	category: {
		options: [
			{
				label: 'archived',
				value: {
					archived: true
				}
			},
			{
				label: 'active',
				value: {
					archived: false
				}
			}
		],
		defaultValue: { archived: false },
		selected: { archived: false }
	},
	studios: {
		options: [],
		defaultValue: [],
		selected: []
	},
	searchText: ''
};

function Projects() {
	const params = new URLSearchParams(new URL(window.location.href).search);
	let BASE_FILTERS = cloneDeep(DEFAULT_FILTERS);
	BASE_FILTERS.category.selected =
		params.get('archived') === 'true'
			? { archived: true }
			: { archived: false };
	BASE_FILTERS.studios.selected = params.get('studios')
		? params.get('studios').split(',')
		: [];
	BASE_FILTERS.searchText = params.get('searchText') || '';
	const [userFilters, setUserFilters] = useState(BASE_FILTERS);
	const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
	const { width } = useWindowSize();
	const isMobileLayout = width <= 640;

	const { session } = useAuth();

	const {
		isInitialLoading: isLoading,
		isError,
		data,
		isFetching,
		isFetched
	} = useQuery(
		['ProjectsPage', userFilters.category.selected.archived, session._id],
		() => api.graphql.request(PROJECTS_QUERY, {
			archived: userFilters.category.selected.archived
		}),
		{
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			// staleTime: 20 * 60 * 1000, // 20 minutes
			cacheTime: 20 * 60 * 1000 // 20 minutes
		}
	);

	const inputSearchBarRef = useRef(null);
	const cardGridRef = useRef(null);

	useEffect(() => {
		if(inputSearchBarRef?.current?.value) {
			inputSearchBarRef.current.focus();
		}
	}, [userFilters]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			const tag = e.target.tagName;
			const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;

			if(e.key === '/' && !isTyping) {
				e.preventDefault();
				inputSearchBarRef.current?.focus();
			}

			if(e.key === 'Tab' && document.activeElement === inputSearchBarRef.current) {
				e.preventDefault();
				cardGridRef.current?.querySelector('a[href]')?.focus();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	const handleGridKeyDown = (e) => {
		const cards = Array.from(cardGridRef.current?.querySelectorAll('a[href]') || []);
		const idx = cards.indexOf(document.activeElement);
		if(idx === -1) {
			return;
		}

		let next = idx;
		if(e.key === 'ArrowRight') {
			next = idx + 1;
		}
		else if(e.key === 'ArrowLeft') {
			next = idx - 1;
		}
		else if(e.key === 'ArrowDown') {
			next = idx + projectGridColumns;
		}
		else if(e.key === 'ArrowUp') {
			next = idx - projectGridColumns;
		}
		else {
			return;
		}

		e.preventDefault();
		cards[Math.max(0, Math.min(next, cards.length - 1))]?.focus();
	};

	const popstateHandler = () => {
		let params = new URLSearchParams(new URL(window.location.href).search);
		let searchText = params.get('searchText') || '';
		let category =
			params.get('archived') === 'true'
				? { archived: true }
				: { archived: false };
		let studios = params.get('studios') ? params.get('studios').split(',') : [];

		updateFilters({
			...userFilters,
			searchText: searchText,
			category: {
				...userFilters.category,
				selected: category
			},
			studios: {
				...userFilters.studios,
				selected: studios
			}
		});
	};

	useEffect(() => {
		window.addEventListener('popstate', popstateHandler);
		return () => {
			window.removeEventListener('popstate', popstateHandler);
		};
	}, []);

	let studios = data?.studios?.nodes || [];
	let projects = data?.projects?.nodes || [];

	const studioOptions = useMemo(
		() => buildStudioOptions({ projects, studios }),
		[projects, studios]
	);

	const filters = {
		...BASE_FILTERS,
		category: {
			...BASE_FILTERS.category,
			...userFilters.category
		},
		studios: {
			...BASE_FILTERS.studios,
			...userFilters.studios,
			options: studioOptions
		}
	};

	const filteredProjects = useMemo(
		() => filterProjects(projects, userFilters),
		[projects, userFilters]
	);

	const handleFiltersChange = (category, value) => {
		updateFilters({
			...userFilters,
			[category]: {
				...userFilters[category],
				selected: value
			}
		});
	};

	const onInput = (val) => {
		updateFilters({
			...userFilters,
			searchText: val
		});
	};

	const resetFilters = () => {
		window.history.pushState(null, null, new URLSearchParams().toString());
		setUserFilters(cloneDeep(DEFAULT_FILTERS));
		setIsMobileFiltersOpen(false);
	};

	const buildParams = (data) => {
		return new URLSearchParams({
			archived: data.category.selected.archived,
			studios: data.studios.selected.join(','),
			searchText: data.searchText
		}).toString();
	};

	const updateFilters = (filters) => {
		let params =
			buildParams(filters) !== buildParams(DEFAULT_FILTERS)
				? '?' + buildParams(filters)
				: '';
		window.history.pushState(null, null, params);
		setUserFilters(filters);
	};

	const hasCustomFilters =
		buildParams(userFilters) !== buildParams(DEFAULT_FILTERS);
	const showSkeleton = isLoading || (isFetching && !isFetched);
	const hasProjects = filteredProjects.length > 0;
	const projectGridColumns = getProjectGridColumns(width);

	useEffect(() => {
		if(!isMobileLayout) {
			setIsMobileFiltersOpen(false);
		}
	}, [isMobileLayout]);

	return (
		<div
			id="projects"
			data-cy="projects-index"
			data-testid="projects-index"
			className="relative grid min-w-0 bg-background"
			style={{ gridTemplateRows: isMobileLayout ? 'auto auto 1fr' : '5rem 3rem 1fr' }}
		>
			{!isLoading && (
				<>
					<div className="flex flex-nowrap items-center justify-start gap-2 bg-background px-6 py-3 md:flex-wrap md:px-4 md:pb-2 md:pt-4 md:h-auto sm:px-4 sm:pt-3 sm:pb-3" style={{ height: isMobileLayout ? 'auto' : '5rem' }}>
						{filters.studios.options?.length > 0 && !isMobileLayout && (
							<ProjectsFilters
								filters={filters}
								onFiltersChange={handleFiltersChange}
							/>
						)}

						<div className={isMobileLayout ? 'min-w-0 flex-1' : 'min-w-0 flex-none'}>
							<SearchBar
								key={filters.searchText}
								initial={filters.searchText}
								onInput={onInput}
								data-cy="projects-search"
								inputRef={inputSearchBarRef}
								className={isMobileLayout ? 'w-full' : ''}
								inputClassName={isMobileLayout ? 'sm:w-full' : ''}
								style={isMobileLayout ? { width: '100%' } : { width: '60rem' }}
							/>
						</div>

						{filters.studios.options?.length > 0 && isMobileLayout && (
							<Button
								type="button"
								variant="outline"
								size="icon"
								onClick={() => setIsMobileFiltersOpen(true)}
								data-cy="projects-open-filters"
								aria-label="Open filters"
								className="h-11 w-11 shrink-0"
							>
								<SlidersHorizontal className="h-4 w-4" />
							</Button>
						)}
					</div>

					{!isMobileLayout && (
						<div className="flex h-12 flex-row items-start justify-start gap-2 bg-background px-6 md:h-auto md:flex-wrap md:items-center md:px-4 md:pb-3" style={{ height: '3rem' }}>
							<span className="font-bold">
								Showing {filteredProjects.length} result
								{filteredProjects.length !== 1 ? 's' : ''}
							</span>

							{hasCustomFilters && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={resetFilters}
									data-cy="projects-reset-filters"
									className="text-xs"
								>
									Clear filters
								</Button>
							)}
						</div>
					)}
				</>
			)}

			{isMobileLayout && filters.studios.options?.length > 0 && isMobileFiltersOpen && (
				<div className="fixed inset-0 z-50">
					<button
						type="button"
						className="absolute inset-0 h-full w-full border-0 bg-black bg-opacity-70 p-0"
						onClick={() => setIsMobileFiltersOpen(false)}
						aria-label="Close filters"
					/>
					<div className="relative flex h-full w-full flex-col bg-midnight-100 text-foreground">
						<div className="flex items-center justify-between gap-3 px-6 pt-6">
							<h2 className="m-0 text-2xl font-medium">Filter Projects</h2>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => setIsMobileFiltersOpen(false)}
								aria-label="Close filters"
							>
								<X className="h-5 w-5" />
							</Button>
						</div>
						<div className="flex-1 overflow-y-auto px-6 pb-6 pt-8">
							<ProjectsFilters
								filters={filters}
								onFiltersChange={handleFiltersChange}
								inline
							/>
						</div>
						<div className="flex flex-col-reverse items-stretch gap-3 px-6 pb-6" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
							{hasCustomFilters && (
								<Button
									type="button"
									variant="outline"
									onClick={resetFilters}
								>
									Clear filters
								</Button>
							)}
							<Button
								type="button"
								onClick={() => setIsMobileFiltersOpen(false)}
								className="w-full"
							>
								Apply filters
							</Button>
						</div>
					</div>
				</div>
			)}

			<div
				className="min-w-0 overflow-y-auto bg-background"
				style={{ height: isMobileLayout ? 'auto' : 'calc(100vh - 8rem)', minHeight: isMobileLayout ? 'calc(100vh - 10rem)' : undefined }}
			>
				{showSkeleton && (
					<div className="flex h-full w-full items-center justify-center" style={{ minHeight: '18rem' }}>
						<Spinner className="h-auto" />
					</div>
				)}

				{!isLoading && (isError || !data) && (
					<div className="mt-24">
						<LoadError />
					</div>
				)}

				{!isLoading && !isError && (
					<>
						{hasProjects
							? (
								<div
									ref={cardGridRef}
									className="grid gap-4 bg-background px-6 pb-8 pt-2 sm:gap-4 sm:px-3 sm:pb-5"
									style={{ gridTemplateColumns: `repeat(${projectGridColumns}, minmax(0, 1fr))` }}
									onKeyDown={handleGridKeyDown}
								>
									{filteredProjects.map((project) => (
										<CardFocusWrapper key={project.slug}>
											<Link
												to={{ state: 'project', params: { projectId: project.id } }}
											>
												<ProjectCard project={project} />
											</Link>
										</CardFocusWrapper>
									))}
								</div>
							)
							: (
								<Card className="border-0 bg-card bg-opacity-60 shadow-none">
									<CardContent className="flex flex-col items-center justify-center gap-3 text-center" style={{ minHeight: '16rem' }}>
										<h2 className="text-xl font-semibold text-card-foreground">
										No projects match these filters
										</h2>
										<p className="max-w-md text-sm text-muted-foreground">
										Adjust the studio or search query to see more results, or
										clear the current filters.
										</p>
										{hasCustomFilters && (
											<Button
												type="button"
												variant="outline"
												onClick={resetFilters}
											>
												Reset filters
											</Button>
										)}
									</CardContent>
								</Card>
							)}
					</>
				)}
			</div>
		</div>
	);
}

function getProjectGridColumns(width) {
	if(width >= 2400) {
		return 9;
	}

	if(width >= 1900) {
		return 8;
	}

	if(width >= 1200) {
		return 6;
	}

	if(width >= 900) {
		return 5;
	}

	if(width >= 640) {
		return 4;
	}

	return 2;
}

export default Projects;
